import { useTable } from "@refinedev/antd";
import { List, Table, Space, Form, Button, Typography, Collapse, Select, Input, Row, Col, DatePicker } from "antd";
import { useApiUrl, useCustom } from "@refinedev/core";
import { useMemo } from "react";
import { EyeOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import UserSearchSelect from "../../components/UserSearchSelect";

export function PayoutsList() {
  const { Title } = Typography;
  const navigate = useNavigate();
  const apiUrl = useApiUrl();

  const { tableProps, searchFormProps } = useTable({
    syncWithLocation: true,
    resource: "payout", // calling custom endpoints for list
    onSearch: (values: any) => {
      return [
        { field: "q", operator: "contains", value: values.q },
        { field: "status", operator: "eq", value: values.status },
        { field: "instructorID", operator: "eq", value: values.instructorID },
        { field: "startDate", operator: "eq", value: values.dateRange?.[0]?.toISOString() },
        { field: "endDate", operator: "eq", value: values.dateRange?.[1]?.toISOString() },
      ];
    },
    queryOptions: {
      enabled: false,
    },
  });

  const { data, isLoading, refetch: refetchPayouts } = useCustom<{ list: any[]; total: number }>({
    url: `${apiUrl}/payout/list`,
    method: "get",
    config: {
      query: (() => {
        const pagination = tableProps.pagination !== false ? tableProps.pagination : undefined;
        const dateRange = searchFormProps?.form?.getFieldValue('dateRange');
        return {
          _start:
            pagination?.current && pagination?.pageSize
              ? (pagination.current - 1) * pagination.pageSize
              : 0,
          _end:
            pagination?.current && pagination?.pageSize
              ? pagination.current * pagination.pageSize
              : 10,
          q: searchFormProps?.form?.getFieldValue('q'),
          status: searchFormProps?.form?.getFieldValue('status'),
          instructorID: searchFormProps?.form?.getFieldValue('instructorID'),
          startDate: dateRange?.[0]?.toISOString(),
          endDate: dateRange?.[1]?.toISOString(),
        };
      })(),
    },
  });

  const payouts = useMemo(() => data?.data?.list ?? [], [data]);
  const total = data?.data?.total ?? payouts.length;

  const columns = [
    {
      title: "Payout ID",
      dataIndex: "id",
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      render: (v: string) => v ? new Date(v).toLocaleString() : '-',
    },
    {
      title: "Instructor",
      dataIndex: ["instructor", "profile", "fullName"],
      render: (value: string, record: any) => (
        <a onClick={() => navigate(`/users/${record.instructorID}`)}>
          {value || record.instructor?.username || record.instructor?.email || record.instructorID}
        </a>
      )
    },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (v: string) => `$${v}`,
    },
    {
      title: "Status",
      dataIndex: "status",
    },
    {
      title: "Failure Reason",
      dataIndex: "failureReason",
      render: (v: string, r: any) => {
        if (r.status !== "FAILED" || !v) return "-";
        const text = String(v);
        return text.length > 80 ? `${text.slice(0, 80)}...` : text;
      },
    },
    {
      title: "Actions",
      render: (_: any, r: any) => (
        <Space>
          <Button icon={<EyeOutlined />} onClick={() => navigate(`/subscriptions/payouts/${r.id}`)} />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 8 }}>
      <div style={{ display: "flex", gap: 10, justifyContent: "space-between", alignItems: "center" }}>
        <Title level={3} style={{ margin: 0 }}>Payouts</Title>
        <Button icon={<ReloadOutlined />} onClick={() => refetchPayouts()} />
      </div>

      <div style={{ marginTop: 12 }}>
        <Collapse defaultActiveKey={["filters"]}>
          <Collapse.Panel header="Filters" key="filters">
            <Form {...searchFormProps} layout="vertical" size="middle">
              <Row gutter={[16, 8]} align="bottom">

                <Col xs={24} sm={12} md={12} lg={6} xl={5}>
                  <Form.Item name="instructorID" label="Instructor" style={{ marginBottom: 0 }}>
                    <UserSearchSelect onChange={(v:any)=>searchFormProps.form?.setFieldValue('instructorID', v)} userType={[]} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={4} xl={3}>
                  <Form.Item name="status" label="Status" style={{ marginBottom: 0 }}>
                    <Select allowClear style={{ width: "100%" }}>
                      <Select.Option value="PENDING">Pending</Select.Option>
                      <Select.Option value="PROCESSING">Processing</Select.Option>
                      <Select.Option value="PAID">Paid</Select.Option>
                      <Select.Option value="FAILED">Failed</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={5} xl={4}>
                  <Form.Item name="q" label="Search" style={{ marginBottom: 0 }}>
                    <Input placeholder="Search payout ID" allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={6} xl={6}>
                  <Form.Item name="dateRange" label="Date Range" style={{ marginBottom: 0 }}>
                    <DatePicker.RangePicker 
                      style={{ width: "100%" }}
                      format="YYYY-MM-DD"
                      placeholder={["Start Date", "End Date"]}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={3} xl={6}>
                  <Form.Item label=" " colon={false} style={{ marginBottom: 0 }}>
                    <Space>
                      <Button type="primary" onClick={()=>refetchPayouts()}>Apply</Button>
                      <Button onClick={()=>{ searchFormProps.form?.resetFields(); refetchPayouts(); }}>Reset</Button>
                    </Space>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Collapse.Panel>
        </Collapse>
      </div>
      <List>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={payouts}
          loading={isLoading}
          pagination={{
            ...tableProps.pagination,
            total,
            showTotal: (t) => `Total ${t} items`,
            showSizeChanger: true,
            showQuickJumper: true,
            onChange: () => refetchPayouts(),
          }}
        />
      </List>
    </div>
  );
}


